/**
 * Browser fetch client for the Recruiter BFF API — used by the frontend SPA.
 * Call `setBaseUrl` from Vite env (e.g. `import.meta.env.VITE_API_URL`).
 */
let BASE_URL =
  (typeof import.meta === 'undefined'
    ? undefined
    : (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
        ?.VITE_API_URL) ?? 'http://localhost:3003';

/** Optional — set from Keycloak (or any OIDC) so every BFF request sends Bearer. */
let resolveAccessToken: (() => Promise<string | undefined>) | null = null;

/** Optional — called when any BFF request returns 401 (e.g. trigger re-login). */
let onUnauthorized: (() => void) | null = null;
// Re-entrancy guard — without it, N parallel queries that all 401 each call
// keycloak.login(), racing the redirect and producing a reload loop.
let unauthorizedInFlight = false;

export function setAccessTokenProvider(fn: (() => Promise<string | undefined>) | null) {
  resolveAccessToken = fn;
}

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
  if (!fn) {
    unauthorizedInFlight = false;
  }
}

function notifyUnauthorized(): void {
  if (unauthorizedInFlight || !onUnauthorized) return;
  unauthorizedInFlight = true;
  try {
    onUnauthorized();
  } catch (err) {
    unauthorizedInFlight = false;
    console.error('[recruiter-api-sdk] unauthorized handler threw', err);
  }
}

export function setBaseUrl(url: string) {
  BASE_URL = url.replace(/\/+$/, '');
}

export interface RequestConfig<TBody = unknown> {
  method: string;
  url: string;
  params?: Record<string, unknown>;
  data?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Teto de tempo PRÓPRIO desta request, em ms (EU-654 retorno — paridade com
   * o candidate-api-sdk, guard V65). O default de 30s cabe para leituras
   * comuns, mas as buscas de export por programa carregam payloads de dezenas
   * de MB e eram abortadas no meio. `null` desliga o teto; omitido usa o
   * default do ambiente.
   */
  timeoutMs?: number | null;
}

export interface ResponseErrorConfig<T> {
  error: T;
  status: number;
}

function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = new URL(path, BASE_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      // Arrays serialize as repeated keys (`?k=a&k=b`), matching the Zod
      // `union([scalar, array(scalar)])` shape used across BFF/Core query
      // schemas. `String([1,2])` would produce `"1,2"` and break the array
      // branch on the receiving side.
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null) continue;
          url.searchParams.append(k, String(item));
        }
        return;
      }
      url.searchParams.append(k, String(v));
    });
  }
  return url.toString();
}

/**
 * Resolve o teto de tempo efetivo de uma request: o override explícito quando
 * vier (número positivo finito), `null` para desligar, ou o default do
 * ambiente (30s). Exportada para teste — espelho de `resolveRequestTimeoutMs`
 * do candidate-api-sdk (guard V65).
 */
export function resolveRequestTimeoutMs(override?: number | null): number | null {
  if (override === null) return null;
  if (override !== undefined && Number.isFinite(override) && override > 0) return override;

  const meta =
    typeof import.meta !== 'undefined' && import.meta.env && typeof import.meta.env === 'object'
      ? (import.meta.env as Record<string, string | undefined>)
      : {};
  const raw = meta.VITE_API_REQUEST_TIMEOUT_MS;
  const timeoutMs = raw != null && String(raw) !== '' ? Number(raw) : 30_000;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return null;
  return timeoutMs;
}

function resolveFetchSignal(
  userSignal: AbortSignal | undefined,
  timeoutOverride?: number | null
): AbortSignal | undefined {
  const timeoutMs = resolveRequestTimeoutMs(timeoutOverride);
  if (timeoutMs === null) {
    return userSignal;
  }
  const TimeoutSignal =
    typeof AbortSignal !== 'undefined'
      ? (AbortSignal as typeof AbortSignal & { timeout?: (ms: number) => AbortSignal }).timeout
      : undefined;
  if (typeof TimeoutSignal !== 'function') {
    return userSignal;
  }
  const timeoutSignal = TimeoutSignal(timeoutMs);
  if (!userSignal) {
    return timeoutSignal;
  }
  const anyFn = (
    AbortSignal as typeof AbortSignal & { any?: (signals: AbortSignal[]) => AbortSignal }
  ).any;
  if (typeof anyFn === 'function') {
    return anyFn([userSignal, timeoutSignal]);
  }
  return userSignal;
}

export async function fetchClient<TData, TError = unknown, TBody = unknown>(
  config: RequestConfig<TBody>
): Promise<{ data: TData; error: null } | { data: null; error: ResponseErrorConfig<TError> }> {
  const { method, url, params, data, headers: customHeaders, signal, timeoutMs } = config;
  const isBodyMethod = !['GET', 'HEAD'].includes(method.toUpperCase());

  // FormData / Blob bodies must pass through verbatim — the browser sets
  // `Content-Type: multipart/form-data; boundary=...` automatically and
  // serializing them via JSON.stringify silently produces `{}`.
  const isMultipartBody = typeof FormData !== 'undefined' && data instanceof FormData;
  const isBlobBody = typeof Blob !== 'undefined' && data instanceof Blob;
  const isRawBody = isMultipartBody || isBlobBody;

  const headers: Record<string, string> = {
    ...(isBodyMethod && !isRawBody ? { 'content-type': 'application/json' } : {}),
    ...(customHeaders ?? {}),
  };

  const token = resolveAccessToken ? await resolveAccessToken() : undefined;
  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  try {
    const res = await fetch(buildUrl(url, params), {
      method,
      headers,
      ...(isBodyMethod && data != null
        ? { body: isRawBody ? (data as BodyInit) : JSON.stringify(data) }
        : {}),
      signal: resolveFetchSignal(signal, timeoutMs),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    if (!res.ok) {
      if (res.status === 401) {
        notifyUnauthorized();
      }
      const errorData = isJson ? await res.json() : await res.text();
      return { data: null, error: { error: errorData as TError, status: res.status } };
    }

    const responseData = isJson ? await res.json() : await res.text();
    return { data: responseData as TData, error: null };
  } catch (err) {
    return {
      data: null,
      error: { error: { message: (err as Error).message } as TError, status: 0 },
    };
  }
}

export type Client = typeof fetchClient;

export default fetchClient;
